<template>
    <section class="section">
        <div class="container-fluid">
            <div class="columns">
                <div class="column is-12">
                    <h1 class="title">{{ translate("invitation_links.title") }}</h1>
                </div>
            </div>

            <div class="columns is-multiline is-centered">
                <div class="column is-12" v-if="initDone">
                    <div class="buttons">
                        <button :disabled="addingEnabled" class="button is-primary mr-3" @click="enableAddingMode">
                            <span class="icon">
                                <i class="fa fa-plus" />
                            </span>
                            <span>
                                {{ translate("invitation_links.create_new") }}
                            </span>
                        </button>

                        <template v-if="addingEnabled">
                            <div class="control is-flex is-align-items-center">
                                <label class="label mr-2">{{ translate("invitation_links.expires") }}:</label>
                                <div class="select mb-2 mr-2">
                                    <select v-model="expirationTime">
                                        <option :value="1800">{{ translate("common.duration.30_min") }}</option>
                                        <option :value="3600">{{ translate("common.duration.1_hour") }}</option>
                                        <option :value="2 * 3600">{{ translate("common.duration.2_hours") }}</option>
                                        <option :value="4 * 3600">{{ translate("common.duration.4_hours") }}</option>
                                        <option :value="8 * 3600">{{ translate("common.duration.8_hours") }}</option>
                                    </select>
                                </div>
                            </div>

                            <LoadingButton class="button is-success" @click="createNewLink" :is-loading="addingInProgress">
                                {{ translate("common.ok") }}
                            </LoadingButton>
                            <button class="button is-danger" @click="cancelAddingMode">
                                {{ translate("common.cancel") }}
                            </button>
                        </template>
                    </div>
                </div>
                <div class="column is-12" v-if="initDone && listing.invitationLinks.length > 0">
                    <table class="table table-hover is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("invitation_links.link") }}</th>
                                <th>{{ translate("invitation_links.created") }}</th>
                                <th>{{ translate("invitation_links.used_by") }}</th>
                                <th>{{ translate("invitation_links.expires") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(link, index) in listing.invitationLinks" :key="index">
                                <td>
                                    {{ index + 1 }}
                                </td>
                                <td>
                                    <div class="field has-addons">
                                        <div class="control is-expanded">
                                            <input class="input" type="text" :value="getFullLink(link.id)" readonly />
                                        </div>
                                        <div class="control">
                                            <button class="button" @click="copyToClipboard(link.id)">
                                                <span class="icon">
                                                    <i class="fa fa-copy" />
                                                </span>
                                                <span>
                                                    {{ translate("invitation_links.copy") }}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    {{ convertToTimezone(link.created, eventTimezone) }}
                                </td>
                                <td>
                                    <template v-if="!link.userId">
                                        {{ translate("invitation_links.not_used_yet") }}
                                    </template>
                                    <template v-else>
                                        <span class="clarity-mask">{{ getUserData(link.userId) }}</span>
                                    </template>
                                </td>
                                <td>
                                    <template v-if="!link.expires">
                                        {{ translate("invitation_links.not_used_yet") }}
                                    </template>
                                    <template v-else>
                                        <span :class="linkExpirationClass(link.expires)">
                                            {{ convertToTimezone(link.expires, eventTimezone) }}
                                        </span>
                                    </template>
                                </td>
                                <td>
                                    <LoadingButton
                                        class="button is-danger is-small"
                                        @click="deleteLink(link.id)"
                                        :is-loading="deletingInProgress[link.id]"
                                    >
                                        <span class="icon">
                                            <i class="fa fa-times" />
                                        </span>
                                        <span>{{ translate("common.delete") }} </span>
                                    </LoadingButton>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="column is-12" v-if="initDone && listing.invitationLinks.length === 0">
                    <p>
                        {{ translate("invitation_links.no_links") }}
                    </p>
                </div>

                <LoadingSpinner2 v-if="!initDone" class="has-added-margin" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted } from "vue";
    import { DateTime } from "luxon";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import { successToast } from "../Common/toasts";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import invitationLinksApi, { InvitationLinkListing } from "../Api/invitationLinks";
    import { usePromisedRequest } from "../Common/request";

    const props = defineProps<{ eventId: number; eventTimezone: string }>();

    const invitationLinksApiInstance = invitationLinksApi.init(usePromisedRequest);
    const addingInProgress = ref<boolean>(false);
    const addingEnabled = ref<boolean>(false);
    const deletingInProgress = ref({});
    const listing = ref<InvitationLinkListing>({
        invitationLinks: [],
        users: {},
    });
    const expirationTime = ref<number>(4 * 3600);
    const initDone = ref(false);

    const fetchListing = () => {
        return invitationLinksApiInstance.getListing(props.eventId).then(invitationLinksListingResp => {
            listing.value = invitationLinksListingResp;
        });
    };

    function enableAddingMode() {
        addingEnabled.value = true;
    }
    function cancelAddingMode() {
        addingEnabled.value = false;
    }

    function createNewLink() {
        addingInProgress.value = true;
        return invitationLinksApiInstance
            .createNew(props.eventId, expirationTime.value)
            .then(() => {
                successToast(translate("invitation_links.new_link_created"));
                return fetchListing();
            })
            .catch(handleError)
            .finally(() => {
                addingInProgress.value = false;
                addingEnabled.value = false;
            });
    }

    const deleteLink = linkId => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        deletingInProgress.value[linkId] = true;

        return invitationLinksApiInstance
            .deleteLink(props.eventId, linkId)
            .then(fetchListing)
            .catch(handleError)
            .finally(() => {
                deletingInProgress.value[linkId] = false;
            });
    };

    const copyToClipboard = linkId => {
        navigator.clipboard.writeText(getFullLink(linkId)).then(
            () => {
                successToast(translate("invitation_links.copied_to_clipboard"));
            },
            () => {},
        );
    };

    const getFullLink = linkId => {
        return window.location.origin + "/invitation/" + linkId;
    };

    const getUserData = userId => {
        const user = listing.value.users[userId];
        return `${user.firstName} ${user.lastName} (${user.email})`;
    };

    const isLinkExpired = linkExpirationTime => {
        const expirationDate = DateTime.fromSQL(linkExpirationTime, { zone: "UTC" });
        return DateTime.now().toMillis() > expirationDate.toMillis();
    };

    const linkExpirationClass = linkExpirationTime => {
        return isLinkExpired(linkExpirationTime) ? "has-text-danger" : "has-text-success";
    };

    onMounted(() => {
        return fetchListing()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });
</script>

<style scoped>
    .bottom-buffer-20 {
        margin-bottom: 20px;
    }

    .table td {
        vertical-align: middle;
    }
</style>
